// src/pages/VehicleV2/BillGenerationEntry.jsx
// Hire-vehicle BILL ENTRY (vendor bill to pay the owner)
//
// WHERE DATA COMES FROM
// -----------------------------------------------------------------------------
// Listing page        → only picks vehicleId + brtype=H. No amounts typed there.
// GET /init           → bill no, date, party, vehicle master, TDS%, rights.
// BillAddVehicle      → hours/km/breaking/diesel/bata/driver advance + progressIds
// BillAddSpares       → spare issue ids + spare deduction amount
// BillAddAdvance T=1  → advance ids + advance amount
// BillAddAdvance T=2  → shifting ids + shifting amount
// THIS PAGE (typed)   → particulars, TDS%, prepared by, maintenance, material
//                       deduction, other charges/reason, include diesel yes/no,
//                       bata (if child did not lock it)
// POST /submit        → FIRST time bill is written to DB
//                       tbl_Veh_Bill_Generation_Mas + progress/spares/advance/shift
//
// Child pages must write:
//   localStorage[billGen_selection_${vehicleId}]
//   sessionStorage[billGeneration_selectedVehDetails]

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaArrowLeft,
  FaSave,
  FaUndo,
  FaExternalLinkAlt,
  FaSyncAlt,
} from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";
const API_BASE = `${BASE_URL}/api/billGeneration`;
const DEFAULT_ID = 0;
const HIRE_TYPE = "H";

const toNum = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : def;
};
const toInt = (v, def = DEFAULT_ID) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();
const safeJson = (raw, def = null) => {
  try {
    return raw ? JSON.parse(raw) : def;
  } catch {
    return def;
  }
};
const normalizeIds = (arr, def = DEFAULT_ID) =>
  Array.isArray(arr) ? arr.map((x) => toInt(x, def)).filter((x) => x > 0) : [];

const defaultVehicle = {
  vehicleName: "",
  vehicleType: "",
  vehicleBasis: "",
  regNo: "",
  equipmentNo: "",
  usageCost: "0",
  fuelRate: "0",
  tdsPercent: "0",
};
const defaultParty = { name: "", pan: "", address: "" };
const defaultProgress = {
  totalHrs: "0",
  totalKm: "0",
  breakingHrs: "0",
  hrRate: "0",
  kmRate: "0",
  hrAmount: "0",
  kmAmount: "0",
  breakingAmount: "0",
  driverAdvance: "0",
  bata: "0",
  progressTotal: "0",
  fuelIssued: "0",
  totalDieselCost: "0",
};
const defaultEditable = {
  particulars: "",
  advanceAmt: "0",
  shiftingAmt: "0",
  tdsPercent: "0",
  preparedBy: "",
  sparesDeduction: "0",
  materialDeduction: "0",
  maintenanceReason: "",
  maintenanceCharges: "0",
  otherCharges: "0",
  reasonForDeduction: "",
  includeDieselCost: false,
};

const BillGenerationEntry = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const q = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const vehicleId = toStr(q.get("VehID") || q.get("id") || "");
  const vehicleOwnerType = toStr(q.get("brtype") || HIRE_TYPE).toUpperCase();

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "0";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  const STORAGE_KEY = useMemo(() => `billGen_selection_${vehicleId}`, [vehicleId]);

  const [pageLoading, setPageLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [canSubmit, setCanSubmit] = useState(true);
  const [hasRights, setHasRights] = useState(true);

  const [billNo, setBillNo] = useState("");
  const [billDate, setBillDate] = useState("");
  const [party, setParty] = useState(defaultParty);
  const [veh, setVeh] = useState(defaultVehicle);
  const [vehMeta, setVehMeta] = useState({ type: HIRE_TYPE, basis: "", subType: "" });
  const [progress, setProgress] = useState(defaultProgress);

  const [particulars, setParticulars] = useState("");
  const [advanceAmt, setAdvanceAmt] = useState("0");
  const [shiftingAmt, setShiftingAmt] = useState("0");
  const [tdsPercent, setTdsPercent] = useState("0");
  const [preparedBy, setPreparedBy] = useState("");
  const [sparesDeduction, setSparesDeduction] = useState("0");
  const [materialDeduction, setMaterialDeduction] = useState("0");
  const [maintenanceReason, setMaintenanceReason] = useState("");
  const [maintenanceCharges, setMaintenanceCharges] = useState("0");
  const [otherCharges, setOtherCharges] = useState("0");
  const [reasonForDeduction, setReasonForDeduction] = useState("");
  const [includeDieselCost, setIncludeDieselCost] = useState(false);

  const [progressIds, setProgressIds] = useState([]);
  const [progressDetIds, setProgressDetIds] = useState([]);
  const [sparesIds, setSparesIds] = useState([]);
  const [sparesDetIds, setSparesDetIds] = useState([]);
  const [advanceIds, setAdvanceIds] = useState([]);
  const [shiftingIds, setShiftingIds] = useState([]);

  const isBusy = pageLoading || submitLoading || refreshing;

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : `bg-white ${currentSkin.glass} text-black`
      }`,
    [isDark, currentSkin]
  );
  const sectionClass = useMemo(
    () =>
      `rounded-xl p-6 border ${
        isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
      }`,
    [isDark]
  );
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );
  const readBoxClass = useMemo(
    () =>
      `w-full px-3 py-2 text-sm rounded-lg border ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-gray-100"
          : "bg-gray-50 border-gray-200 text-gray-800"
      }`,
    [isDark]
  );
  const labelClass = useMemo(
    () => `block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );
  const miniButtonClass = useMemo(
    () =>
      `inline-flex items-center gap-2 px-3 py-2 rounded-lg font-semibold shadow transition-all text-white ${buttonClass} ${hoverClass}`,
    [buttonClass, hoverClass]
  );
  const summaryPillClass = useMemo(
    () =>
      `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
        isDark ? "bg-blue-900/60 text-blue-100" : "bg-blue-100 text-blue-800"
      }`,
    [isDark]
  );

  /* ---------------- calcs (hire bill) ---------------- */
  // Progress total = hr + km + bata - driver advance
  const liveProgressTotal = useMemo(() => {
    const hr = toNum(progress.hrAmount);
    const km = toNum(progress.kmAmount);
    const bata = toNum(progress.bata);
    const adv = toNum(progress.driverAdvance);
    return hr + km + bata - adv;
  }, [progress.hrAmount, progress.kmAmount, progress.bata, progress.driverAdvance]);

  const sparesTotalAmount = useMemo(
    () => toNum(sparesDeduction) + toNum(materialDeduction),
    [sparesDeduction, materialDeduction]
  );

  // Payable before TDS = progress + other charges - (party advance + shifting + maint + spares)
  const baseBalanceBeforeTds = useMemo(() => {
    return (
      toNum(liveProgressTotal) +
      toNum(otherCharges) -
      (toNum(advanceAmt) + toNum(shiftingAmt) + toNum(maintenanceCharges) + toNum(sparesTotalAmount))
    );
  }, [liveProgressTotal, otherCharges, advanceAmt, shiftingAmt, maintenanceCharges, sparesTotalAmount]);

  const tdsAmount = useMemo(() => {
    const per = toNum(tdsPercent);
    if (per <= 0) return 0;
    return (per * toNum(baseBalanceBeforeTds)) / 100;
  }, [tdsPercent, baseBalanceBeforeTds]);

  const balancePayable = useMemo(() => {
    let bal = toNum(baseBalanceBeforeTds) - toNum(tdsAmount);
    if (includeDieselCost) bal += toNum(progress.totalDieselCost);
    return bal;
  }, [baseBalanceBeforeTds, tdsAmount, includeDieselCost, progress.totalDieselCost]);

  const canUse = useCallback((cond, msg) => {
    if (!cond) toast.error(msg);
    return !!cond;
  }, []);

  const applyVehDetailsToProgress = useCallback((vehDetails) => {
    if (!vehDetails) return;
    const hrAmount = toStr(vehDetails.totalPrgAmount ?? vehDetails.hrAmount ?? "0");
    const kmAmount = toStr(vehDetails.totalamountkm ?? vehDetails.kmAmount ?? "0");
    const bata = toStr(vehDetails.bata ?? "0");
    const driverAdvance = toStr(vehDetails.advance ?? vehDetails.driverAdvance ?? "0");
    setProgress((prev) => ({
      ...prev,
      totalHrs: toStr(vehDetails.totalhour ?? vehDetails.totalHrs ?? prev.totalHrs),
      totalKm: toStr(vehDetails.totalKMHour ?? vehDetails.totalKm ?? prev.totalKm),
      breakingHrs: toStr(vehDetails.breakingHrs ?? prev.breakingHrs),
      hrRate: toStr(vehDetails.hrrateamnt ?? vehDetails.hrRate ?? prev.hrRate),
      kmRate: toStr(vehDetails.rateperkmH ?? vehDetails.kmRate ?? prev.kmRate),
      hrAmount,
      kmAmount,
      breakingAmount: toStr(vehDetails.breakingAmount ?? prev.breakingAmount),
      driverAdvance,
      bata,
      progressTotal: String(toNum(hrAmount) + toNum(kmAmount) + toNum(bata) - toNum(driverAdvance)),
      fuelIssued: toStr(vehDetails.dieselQTY ?? vehDetails.fuelIssued ?? prev.fuelIssued),
      totalDieselCost: toStr(vehDetails.totalDieselAmount ?? vehDetails.totalDieselCost ?? prev.totalDieselCost),
    }));
    sessionStorage.setItem("billGeneration_selectedVehDetails", JSON.stringify(vehDetails));
  }, []);

  const applyIdsFromSelectionObject = useCallback((sel) => {
    if (!sel) return;
    const pIds = normalizeIds(sel.progressIds);
    const pDet = Array.isArray(sel.progressDetIds) ? sel.progressDetIds.map((x) => toInt(x, 0)) : [];
    if (pIds.length) {
      setProgressIds(pIds);
      setProgressDetIds(pDet.length === pIds.length ? pDet : pIds.map(() => DEFAULT_ID));
    }
    const sIds = normalizeIds(sel.sparesIds);
    const sDet = Array.isArray(sel.sparesDetIds) ? sel.sparesDetIds.map((x) => toInt(x, 0)) : [];
    if (sIds.length) {
      setSparesIds(sIds);
      setSparesDetIds(sDet.length === sIds.length ? sDet : sIds.map(() => DEFAULT_ID));
    }
    const aIds = normalizeIds(sel.advanceIds);
    const shIds = normalizeIds(sel.shiftingIds);
    if (aIds.length) setAdvanceIds(aIds);
    if (shIds.length) setShiftingIds(shIds);
    if (sel.advanceAmt != null) setAdvanceAmt(toStr(sel.advanceAmt));
    if (sel.shiftingAmt != null) setShiftingAmt(toStr(sel.shiftingAmt));
    if (sel.sparesDeduction != null) setSparesDeduction(toStr(sel.sparesDeduction));
  }, []);

  const syncFromLocalStorage = useCallback(() => {
    if (!vehicleId) return;
    applyIdsFromSelectionObject(safeJson(localStorage.getItem(STORAGE_KEY), null));
  }, [vehicleId, STORAGE_KEY, applyIdsFromSelectionObject]);

  const loadInit = useCallback(async () => {
    if (!canUse(vehicleId, "Missing vehicleId in URL")) return;
    if (!canUse(toInt(loginUserId), "loginUserId missing")) return;
    if (!canUse(toInt(branchId), "branchId missing")) return;

    try {
      setPageLoading(true);
      const { data } = await axios.get(`${API_BASE}/init`, {
        params: {
          vehicleId,
          branchId,
          loginUserId,
          brtype: vehicleOwnerType || HIRE_TYPE,
        },
        headers: commonHeaders,
      });

      if (!data?.success) throw new Error(data?.message || "Init failed");

      setBillNo(toStr(data.billNo));
      setBillDate(toStr(data.billDate));
      setParty({
        name: toStr(data?.party?.name),
        pan: toStr(data?.party?.pan),
        address: toStr(data?.party?.address),
      });

      const v = data?.vehicle || {};
      setVeh({
        vehicleName: toStr(v.vehicleName),
        vehicleType: toStr(v.vehicleType),
        vehicleBasis: toStr(v.vehicleBasis),
        regNo: toStr(v.regNo),
        equipmentNo: toStr(v.equipmentNo),
        usageCost: toStr(v.usageCost ?? "0"),
        fuelRate: toStr(v.fuelRate ?? "0"),
        tdsPercent: toStr(v.tdsPercent ?? "0"),
      });
      setVehMeta({
        type: toStr(data?.meta?.type || HIRE_TYPE),
        basis: toStr(data?.meta?.basis),
        subType: toStr(data?.meta?.subType),
      });

      // Master TDS only if user has not typed a value yet
      setTdsPercent((prev) => (prev && toNum(prev) > 0 ? prev : toStr(v.tdsPercent ?? "0")));

      // const rights = data?.rights || {};
      // setHasRights(rights.canView !== false && rights.canAccess !== false);
      // setCanSubmit(rights.canAdd !== false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load details");
    } finally {
      setPageLoading(false);
    }
  }, [vehicleId, loginUserId, branchId, vehicleOwnerType, commonHeaders, canUse]);

  const openChild = useCallback((path) => {
    window.open(path, "_blank");
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      syncFromLocalStorage();
      const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
      if (saved) {
        try {
          applyVehDetailsToProgress(JSON.parse(saved));
        } catch {
          /* ignore */
        }
      }
      await loadInit();
      toast.success("Header refreshed. Progress/spares kept from child screens.");
    } catch (err) {
      toast.error(err?.message || "Refresh failed");
    } finally {
      setRefreshing(false);
    }
  }, [syncFromLocalStorage, applyVehDetailsToProgress, loadInit]);

  const handleReset = useCallback(() => {
    setParticulars(defaultEditable.particulars);
    setAdvanceAmt(defaultEditable.advanceAmt);
    setShiftingAmt(defaultEditable.shiftingAmt);
    setMaterialDeduction(defaultEditable.materialDeduction);
    setMaintenanceReason(defaultEditable.maintenanceReason);
    setMaintenanceCharges(defaultEditable.maintenanceCharges);
    setOtherCharges(defaultEditable.otherCharges);
    setReasonForDeduction(defaultEditable.reasonForDeduction);
    setPreparedBy(defaultEditable.preparedBy);
    setIncludeDieselCost(defaultEditable.includeDieselCost);
    setProgress({ ...defaultProgress });
    setProgressIds([]);
    setProgressDetIds([]);
    setSparesIds([]);
    setSparesDetIds([]);
    setAdvanceIds([]);
    setShiftingIds([]);
    setSparesDeduction("0");
    sessionStorage.removeItem("billGeneration_selectedVehDetails");
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    toast.info("Cleared bill cart and typed fields");
  }, [STORAGE_KEY]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!canSubmit) {
        toast.error("You don't have rights to submit.");
        return;
      }
      syncFromLocalStorage();
      if (!progressIds.length) {
        toast.error("Select Vehicle Progress first. Bill rows are created only on Submit.");
        return;
      }

      setSubmitLoading(true);
      const payload = {
        vehicleId,
        branchId,
        loginUserId,
        brtype: vehicleOwnerType || HIRE_TYPE,
        billNo,
        billDate,
        particulars,
        preparedBy,
        advanceAmt,
        shiftingAmt,
        sparesDeduction,
        materialDeduction,
        sparesTotalAmount: String(sparesTotalAmount),
        maintenanceReason,
        maintenanceCharges,
        otherCharges,
        reasonForDeduction,
        tdsPercent,
        tdsAmount: String(tdsAmount),
        includeDieselCost,
        fuelIssued: progress.fuelIssued,
        totalDieselCost: progress.totalDieselCost,
        progress: {
          totalHrs: progress.totalHrs,
          totalKm: progress.totalKm,
          breakingHrs: progress.breakingHrs,
          hrAmount: progress.hrAmount,
          kmAmount: progress.kmAmount,
          breakingAmount: progress.breakingAmount,
          driverAdvance: progress.driverAdvance,
          bata: progress.bata,
          progressTotal: String(liveProgressTotal),
        },
        balancePayable: String(balancePayable),
        progressIds,
        progressDetIds:
          progressDetIds.length === progressIds.length
            ? progressDetIds
            : progressIds.map(() => DEFAULT_ID),
        sparesIds,
        sparesDetIds:
          sparesDetIds.length === sparesIds.length
            ? sparesDetIds
            : sparesIds.map(() => DEFAULT_ID),
        advanceIds,
        shiftingIds,
      };

      const { data } = await axios.post(`${API_BASE}/submit`, payload, { headers: commonHeaders });
      if (!data?.success) throw new Error(data?.message || "Submit failed");

      toast.success(`Bill Generated Successfully (Bill ID ${data.billId || ""})`);
      sessionStorage.removeItem("billGeneration_selectedVehDetails");
      try {
        localStorage.removeItem(STORAGE_KEY);
      } catch {
        /* ignore */
      }
      navigate("/vehicle/BillGenerationListing");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Submit failed");
    } finally {
      setSubmitLoading(false);
    }
  }, [
    canSubmit,
    syncFromLocalStorage,
    progressIds,
    progressDetIds,
    sparesIds,
    sparesDetIds,
    advanceIds,
    shiftingIds,
    vehicleId,
    branchId,
    loginUserId,
    vehicleOwnerType,
    billNo,
    billDate,
    particulars,
    preparedBy,
    advanceAmt,
    shiftingAmt,
    sparesDeduction,
    materialDeduction,
    sparesTotalAmount,
    maintenanceReason,
    maintenanceCharges,
    otherCharges,
    reasonForDeduction,
    tdsPercent,
    tdsAmount,
    includeDieselCost,
    progress,
    liveProgressTotal,
    balancePayable,
    commonHeaders,
    STORAGE_KEY,
    navigate,
  ]);

  useEffect(() => {
    if (!vehicleId) {
      toast.error("Missing vehicleId in URL");
      return;
    }
    loadInit();
    syncFromLocalStorage();
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) syncFromLocalStorage();
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [vehicleId, loadInit, syncFromLocalStorage, STORAGE_KEY]);

  useEffect(() => {
    if (location.state?.vehDetails) applyVehDetailsToProgress(location.state.vehDetails);
    applyIdsFromSelectionObject(location.state);
  }, [location.state, applyVehDetailsToProgress, applyIdsFromSelectionObject]);

  useEffect(() => {
    const saved = sessionStorage.getItem("billGeneration_selectedVehDetails");
    if (!saved) return;
    try {
      applyVehDetailsToProgress(JSON.parse(saved));
    } catch {
      /* ignore */
    }
  }, [applyVehDetailsToProgress]);

  if (!hasRights) {
    return (
      <VehicleLayoutWrapper>
        <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
          <HeaderBar
            title={<span className="font-dmserif text-3xl">BILL GENERATION ENTRY</span>}
            setSkin={setSkin}
            currentSkin={skin}
          />
          <div className={cardClass}>You don&apos;t have rights to access this page.</div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  const Field = ({ label, hint, children }) => (
    <div>
      <label className={labelClass}>
        {label}
        {hint ? <span className="ml-2 text-xs opacity-60 font-normal">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
  const ReadLabel = ({ value }) => <div className={readBoxClass}>{toStr(value) || "—"}</div>;

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BILL GENERATION ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <div>
              <div className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Hired Vehicle Bill Entry
              </div>
              <div className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Vehicle ID: {vehicleId || "-"} • User ID: {loginUserId || "-"}
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                <span className={summaryPillClass}>Bill No: {billNo || "-"}</span>
                <span className={summaryPillClass}>
                  Mode: {vehicleOwnerType === "H" ? "Hire" : vehicleOwnerType}
                </span>
                <span className={summaryPillClass}>
                  Progress rows: {progressIds.length}
                </span>
                <span className={summaryPillClass}>
                  Saved only on Submit
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => navigate("/vehicle/BillGenerationListing")} disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"} ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
              <button type="button" onClick={handleRefresh} disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"} ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button type="button" onClick={handleReset} disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white" : "bg-gray-200 hover:bg-gray-300 text-gray-700"} ${isBusy ? "opacity-70 cursor-not-allowed" : ""}`}>
                <FaUndo /> Reset
              </button>
              <button type="button" onClick={handleSubmit} disabled={isBusy || !canSubmit} className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg text-white ${buttonClass} ${hoverClass} ${isBusy || !canSubmit ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSave /> {submitLoading ? "Submitting..." : "Submit Bill"}
              </button>
            </div>
          </div>

          <div className={`${sectionClass} mb-6 text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            This page does <b>not</b> insert a bill while you type. Header comes from vehicle master.
            Hours / km / spares come from the child screens. Click <b>Submit Bill</b> to write
            <code className="mx-1">tbl_Veh_Bill_Generation_Mas</code>.
          </div>

          <div className={sectionClass}>
            <div className={`text-lg font-semibold mb-5 ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle / Party (from master — not typed here)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Bill No" hint="auto"><ReadLabel value={billNo} /></Field>
              <Field label="PAN Card No"><ReadLabel value={party.pan} /></Field>
              <Field label="Name of the Party"><ReadLabel value={party.name} /></Field>
              <Field label="Mailing Address"><ReadLabel value={party.address} /></Field>
              <Field label="Vehicle Name"><ReadLabel value={veh.vehicleName} /></Field>
              <Field label="Vehicle Basis"><ReadLabel value={veh.vehicleBasis || vehMeta.basis} /></Field>
              <Field label="Vehicle Type"><ReadLabel value={veh.vehicleType} /></Field>
              <Field label="Vehicle (Equipment)"><ReadLabel value={veh.equipmentNo} /></Field>
              <Field label="Vehicle Reg No"><ReadLabel value={veh.regNo} /></Field>
              <Field label="Bill Date"><ReadLabel value={billDate} /></Field>
            </div>
          </div>

          <div className={`${sectionClass} mt-6`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle Progress (entered on Add Vehicle Progress)
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={() => openChild(`/vehicle/BillAddVehicle?VehID=${encodeURIComponent(vehicleId)}&Type=4`)} className={miniButtonClass}>
                  <FaExternalLinkAlt /> Add Vehicle Progress
                </button>
                <button type="button" onClick={() => openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=1`)} className={miniButtonClass}>
                  <FaExternalLinkAlt /> Add Advance
                </button>
                <button type="button" onClick={() => openChild(`/vehicle/BillAddAdvance?VehID=${encodeURIComponent(vehicleId)}&Type=2`)} className={miniButtonClass}>
                  <FaExternalLinkAlt /> Add Shifting
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Total Hrs"><ReadLabel value={progress.totalHrs} /></Field>
              <Field label="Particulars" hint="typed here">
                <textarea rows={3} value={particulars} onChange={(e) => setParticulars(e.target.value)} className={inputClass} placeholder="Enter particulars" />
              </Field>
              <Field label="Total Km"><ReadLabel value={progress.totalKm} /></Field>
              <Field label="Breaking Hours"><ReadLabel value={progress.breakingHrs} /></Field>
              <Field label="Breaking Hours Amount"><ReadLabel value={progress.breakingAmount} /></Field>
              <Field label="Hours Total Amount"><ReadLabel value={progress.hrAmount} /></Field>
              <Field label="Km Total Amount"><ReadLabel value={progress.kmAmount} /></Field>
              <Field label="Driver Advance"><ReadLabel value={progress.driverAdvance} /></Field>
              <Field label="Bata (Others)" hint="editable">
                <input
                  type="text"
                  value={progress.bata}
                  onChange={(e) => setProgress((p) => ({ ...p, bata: e.target.value }))}
                  className={inputClass}
                  placeholder="0"
                />
              </Field>
              <Field label="TDS Deductions (%)" hint="typed here">
                <input type="text" value={tdsPercent} onChange={(e) => setTdsPercent(e.target.value)} className={inputClass} placeholder="0" />
              </Field>
              <Field label="Progress Total"><ReadLabel value={liveProgressTotal.toFixed(2)} /></Field>
              <Field label="TDS Deductions Value"><ReadLabel value={tdsAmount.toFixed(2)} /></Field>
              <Field label="Prepared By" hint="typed here">
                <input type="text" value={preparedBy} onChange={(e) => setPreparedBy(e.target.value)} className={inputClass} placeholder="Prepared by" />
              </Field>
              <Field label="Party Advance (Add Advance screen)"><ReadLabel value={advanceAmt} /></Field>
              <Field label="Shifting Amount"><ReadLabel value={shiftingAmt} /></Field>
            </div>
          </div>

          <div className={`${sectionClass} mt-6`}>
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-5">
              <div className={`text-lg font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>
                Spares / Deductions
              </div>
              <button type="button" onClick={() => openChild(`/vehicle/BillAddSpares?VehID=${encodeURIComponent(vehicleId)}&Type=3`)} className={miniButtonClass}>
                <FaExternalLinkAlt /> Add Spares
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Spares Deductions"><ReadLabel value={sparesDeduction} /></Field>
              <Field label="Spares Total"><ReadLabel value={sparesTotalAmount.toFixed(2)} /></Field>
              <Field label="Maintenance Reason" hint="typed here">
                <input type="text" value={maintenanceReason} onChange={(e) => setMaintenanceReason(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Maintenance Charges" hint="typed here">
                <input type="text" value={maintenanceCharges} onChange={(e) => setMaintenanceCharges(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Material Deductions" hint="typed here">
                <input type="text" value={materialDeduction} onChange={(e) => setMaterialDeduction(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Reason for Deduction" hint="typed here">
                <input type="text" value={reasonForDeduction} onChange={(e) => setReasonForDeduction(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Other Charges" hint="typed here">
                <input type="text" value={otherCharges} onChange={(e) => setOtherCharges(e.target.value)} className={inputClass} />
              </Field>
              <Field label="Usage Cost Per Hr/KM"><ReadLabel value={veh.usageCost} /></Field>
              <Field label="Fuel Issued"><ReadLabel value={progress.fuelIssued} /></Field>
              <Field label="Fuel Rate"><ReadLabel value={veh.fuelRate} /></Field>
              <Field label="Include Diesel Cost on this bill" hint="typed here">
                <div className="flex items-center gap-6 pt-2">
                  <label className="flex items-center gap-2">
                    <input type="radio" name="diesel" checked={includeDieselCost === true} onChange={() => setIncludeDieselCost(true)} />
                    <span>Yes</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name="diesel" checked={includeDieselCost === false} onChange={() => setIncludeDieselCost(false)} />
                    <span>No</span>
                  </label>
                </div>
              </Field>
              <Field label="Total Diesel Cost"><ReadLabel value={progress.totalDieselCost} /></Field>
            </div>

            <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className="text-xs uppercase opacity-60">Base Before TDS</div>
                <div className="text-lg font-bold mt-1">{baseBalanceBeforeTds.toFixed(2)}</div>
              </div>
              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className="text-xs uppercase opacity-60">TDS Amount</div>
                <div className="text-lg font-bold mt-1">{tdsAmount.toFixed(2)}</div>
              </div>
              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className="text-xs uppercase opacity-60">Diesel Included</div>
                <div className="text-lg font-bold mt-1">{includeDieselCost ? "Yes" : "No"}</div>
              </div>
              <div className={`rounded-xl p-4 ${isDark ? "bg-gray-900/50" : "bg-white border border-gray-200"}`}>
                <div className="text-xs uppercase opacity-60">Balance Payable</div>
                <div className="text-lg font-bold mt-1">{balancePayable.toFixed(2)}</div>
              </div>
            </div>
          </div>

          {isBusy && (
            <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
              {pageLoading ? "Loading header..." : refreshing ? "Refreshing..." : submitLoading ? "Writing bill to database..." : "Loading..."}
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BillGenerationEntry;